import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AccountType, getAccountTypes } from '../models/account-type';
import { FilterType, setFilterForm } from '../models/filter-input';
import { UserNotification } from '../models/notification';
import { PaginationParams } from '../models/pagination-params';
import { SpinnerConfig } from '../models/spinner-config';
import {
    RowActionType,
    setFilterSelectOptions,
    setTableControls,
    SortDetails,
    TableConfig,
} from '../models/table-config';
import { DialogService } from '../services/dialog.service';
import { NotificationService } from '../services/notification.service';
import { Store } from '../store';
import { notifTypes } from './notif-types';
import { AlertConfirmable } from '../decorators/alert-confirmable.decorator';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    public notifications: UserNotification[];
    public unreadNotificationsCount: number;
    public chatsAwaitingReplyCount: number;
    public types: AccountType[];
    public tableConfig: TableConfig<UserNotification>;
    public updatePaginationParams: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public filterControlForm: FormGroup = new FormGroup({});

    public paginationParams: PaginationParams = {
        limit: 25,
        limits: [25, 50, 75, 100],
        page: 0,
        pages: 0,
        pageArray: [],
        totalRecords: 0,
        sort: 'contacts.firstname',
        sorts: [],
    };

    // Can remove endpointParams when endpoint accepts paginationParams object

    public endpointParams: any = {
        params: {
            content: '',
            subject: '',
            userId: '',
            type: '',
            date: '',
            sort: 'id DESC',
            page: 0,
            limit: 20,
        },
    };

    public spinnerConfig = {
        isGlobalSpinner: false,
        showSpinner: true,
    };

    private _subscriptions: Subscription = new Subscription();

    user = this._store.selectForLocal('user');

    constructor(
        private _notificationService: NotificationService,
        private _dialogService: DialogService<UserNotification>,
        private _toastrService: ToastrService,
        private _store: Store
    )
    {
    }

    ngOnInit(): void
    {
        this.types = getAccountTypes();
        this._getNotifications();
        this._getChatCount();
    }

    ngOnDestroy(): void
    {
        this._subscriptions.unsubscribe();
    }

    public markAs(isMarkAsReadEvent: boolean)
    {
        const markAsFunction = isMarkAsReadEvent
            ? this._notificationService.markAllAsRead()
            : this._notificationService.markAllAsRead();

        markAsFunction.pipe(take(1)).subscribe();
    }

    public deleteNotification(notification: UserNotification)
    {
        this._subscriptions.add(
            this._dialogService
                .confirm()
                .pipe(
                    take(1),
                    filter((confirmValue) => confirmValue !== 'cancel'),
                    switchMap(() =>
                    {
                        const deletedNotification = this.notifications.find(
                            (notificationOne) => notificationOne.id === notification.id
                        );

                        const deletedNotifcationIndex = this.notifications.indexOf(deletedNotification);

                        this.notifications.splice(deletedNotifcationIndex, 1);
                        this.unreadNotificationsCount = this.unreadNotificationsCount - 1;

                        this._toastrService.show(deletedNotification.subject, 'Deleted notification:');

                        return this._notificationService.delete(notification.id);
                    })
                )
                .subscribe()
        );
    }

    public paginate(latestPaginationParams?: PaginationParams)
    {
        if (latestPaginationParams) {
            this.endpointParams.params.page = latestPaginationParams.page;
            this.paginationParams.page = latestPaginationParams.page;
            this.endpointParams.params.limit = latestPaginationParams.limit;
        } else {
            this.endpointParams.params.page = 0;
            this.paginationParams.page = 0;
        }

        this._getNotifications();
    }

    public sort(sortDetails: SortDetails)
    {
        this.endpointParams.params.sort = sortDetails.tableHeader.sortId + ' ' + sortDetails.sortDirection;

        this._getNotifications();
    }

    @AlertConfirmable({ title: 'Are you sure you want to delete all notifications?' })
    confirmAction()
    {
        this._notificationService.deleteAll().subscribe(() =>
        {
            this.unreadNotificationsCount = 0;
            this.notifications.splice(0, this.notifications.length);
        });
        this._toastrService.success('Notifications deleted successfully');
    }

    private _updatePaginationPageCount()
    {
        this.paginationParams.totalRecords = this.unreadNotificationsCount;
        this.updatePaginationParams.next(true);
    }

    private _setNotificationTableConfig()
    {
        this.tableConfig = {
            rowActions: [RowActionType.Delete],
            showButtons: false,
            noResultsFound: 'No Notifications Found',
            isFilterable: true,
            headers: [
                {
                    name: 'Received',
                    isLarge: false,
                    filterInput: {
                        dbId: 'date',
                        type: FilterType.date,
                        placeholder: 'Date',
                    }
                },
                {
                    name: 'User',
                    isLarge: false,
                    filterInput: {
                        dbId: 'user',
                        type: FilterType.default,
                        options: [],
                        placeholder: 'Filter by user',
                    }
                },
                {
                    name: 'Notification Type',
                    isLarge: false,
                    filterInput: {
                        dbId: 'type',
                        type: FilterType.select,
                        options: [
                            ...notifTypes.map((item) => ({
                                viewValue: item.value,
                                value: item.id,
                            })),
                            { viewValue: 'All', value: '' },
                        ],
                        placeholder: '',
                    }
                },
                {
                    name: 'Alert Type',
                    isLarge: false,
                    filterInput: {
                        dbId: 'subject',
                        type: FilterType.default,
                        options: [],
                        placeholder: '',
                    }
                },
                {
                    name: 'Detail',
                    isLarge: true,
                    filterInput: {
                        dbId: 'content',
                        type: FilterType.default,
                        placeholder: 'Filter by content',
                    }
                },
            ],
            data: this.notifications,
            properties: [
                { name: 'date', isDate: true, titleCase: false },
                { name: 'user', isDate: false, titleCase: true },
                { name: 'type', isDate: false, titleCase: true },
                { name: 'subject', isDate: false, titleCase: true },
                { name: 'content', isDate: false, titleCase: true },
            ],
        };

        setFilterForm(this.tableConfig.headers, this.filterControlForm);
        setTableControls(this.filterControlForm, this.tableConfig.headers);
        this.filterControlForm.valueChanges
            .pipe(
                debounceTime(250),
                tap((value) =>
                {
                    this.endpointParams.params.content = value.content;
                    this.endpointParams.params.subject = value.subject;
                    this.endpointParams.params.user = value.user;
                    this.endpointParams.params.type = value.type;
                    this.endpointParams.params.date = this._getDateString(value.date);
                    this.paginate();
                })
            )
            .subscribe();
    }

    private findNotifType(id: string)
    {
        const foundItem = notifTypes.find((item) => item.id === id);
        if (foundItem) {
            return foundItem.value;
        }
        return '';
    }

    private _getNotifications()
    {
        this._subscriptions.add(
            this._notificationService
                .getNotifications(this.endpointParams)
                .pipe(
                    take(1),
                    map((notifications) =>
                    {
                        this.unreadNotificationsCount = notifications.data.totalRecords;
                        this._updatePaginationPageCount();

                        return notifications.data.data.map((notification) =>
                        {
                            return {
                                id: notification.id,
                                user: `${notification?.user?.contact?.firstname || ''} ${
                                    notification?.user?.contact?.surname || ''
                                }`,
                                type: this.findNotifType(notification.type),
                                date: notification.sentAt,
                                subject: notification.subject,
                                content: notification.content,
                            };
                        });
                    }),
                    tap((notifications) =>
                    {
                        this.notifications = notifications;
                        this.spinnerConfig.showSpinner = false;
                        if (!this.tableConfig) {
                            this._setNotificationTableConfig();
                        } else {
                            this.tableConfig.data = this.notifications as any;
                        }
                    })
                )
                .subscribe()
        );
    }

    private _getChatCount()
    {
        const chatStore = this._store.select('chatStore');

        this._subscriptions.add(
            chatStore
                .pipe(
                    tap((value) =>
                    {
                        this.chatsAwaitingReplyCount = value['groups'].length;
                    })
                )
                .subscribe()
        );
    }

    private _getDateString(date: Date): string
    {
        if (!date) {
            return undefined;
        }
        const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        return `${date.getFullYear()}-${month}-${day}`;
    }
}
